import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@charisol-spark/ui";

/**
 * Marketing-site error boundary.
 *
 * Deliberately thinner than the app's: it renders a way out and logs to the
 * console, and does not report the crash to the API. The app's boundary submits
 * through the shared feedback service, which reaches the API client and the
 * environment config — three modules this bundle has no other reason to carry,
 * on pages that make no API calls at all.
 *
 * If marketing crashes ever need reporting, send them with a bare fetch rather
 * than reintroducing the service layer.
 */
interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Marketing site error:", error, info.componentStack);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-4 text-center">
        <h1 className="text-3xl font-bold tracking-tight">
          Something went wrong
        </h1>
        <p className="max-w-md text-muted-foreground">
          Sorry — this page failed to load. Reloading usually fixes it.
        </p>
        <div className="mt-2 flex gap-3">
          <Button onClick={() => window.location.reload()} pill>
            Reload page
          </Button>
          <Button
            variant="outline"
            pill
            onClick={() => window.location.assign("/")}
          >
            Back to home
          </Button>
        </div>
      </div>
    );
  }
}
